/**
 * Settings → Trading: what holds for every worker unless its own plan says otherwise — the limits a new
 * plan is held to, how a position is protected, how a buy zone is watched, and the fee plan the
 * estimates use. Every change is saved as it is made.
 */
import { useEffect, useState } from 'react'
import { COMMISSION_PLANS, COMMISSION_PLAN_LABEL, type CommissionPlan } from '@shared/fees'
import { DEFAULT_BAND, DEFAULT_INSURANCE, DEFAULT_WORKER_LIMITS, type BandSetting, type BandSide, type InsuranceSetting, type LimitSetting, type WorkerLimits } from '@shared/worker'
import { workersApi } from './api'
import { Switch } from './kit'
import { Choose } from './Page'
import { Group, Num, Row, Rows } from './rows'
import { useDemo } from './store'

const SIDES: readonly (readonly [BandSide, string])[] = [
  ['wait', 'wait'],
  ['notify', 'tell me'],
  ['skipDay', 'skip the day'],
  ['pause', 'pause'],
  ['cancel', 'cancel'],
]
const pick = <T extends number>(list: readonly T[], unit: string) => list.map((n) => [`${n}`, `${n}${unit}`] as const)

export function TradingSettings() {
  const admin = useDemo((s) => s.user?.role === 'admin')
  const settings = useDemo((s) => s.settings)
  const save = useDemo((s) => s.saveSettings)
  const say = useDemo((s) => s.say)
  const [limits, setLimits] = useState<WorkerLimits | null>(null)
  useEffect(() => {
    void workersApi.limits().then(setLimits, () => setLimits(DEFAULT_WORKER_LIMITS))
  }, [])
  const putLimits = (next: WorkerLimits) => {
    const was = limits
    setLimits(next)
    void workersApi.setLimits(next).then(setLimits, (e) => (setLimits(was), say(e instanceof Error ? e.message : 'That did not work')))
  }
  const ins: InsuranceSetting = { ...DEFAULT_INSURANCE, ...(settings?.insurance ?? {}) }
  const stall = { ...DEFAULT_INSURANCE.stall!, ...(ins.stall ?? {}) }
  const putIns = (p: Partial<InsuranceSetting>) => void save({ insurance: { ...ins, stall, ...p } })
  const band: BandSetting = { ...DEFAULT_BAND, ...(settings?.band ?? {}) }
  const putBand = (p: Partial<BandSetting>) => void save({ band: { ...band, ...p } })
  const plan: CommissionPlan = settings?.commissionPlan ?? 'fixed'

  /** One limit: a share of the account, and whether it is held. */
  const limit = (key: 'maxWorkerRiskPct' | 'maxOpenRiskPct' | 'maxSymbolExposurePct' | 'maxDailyLossPct', title: string, sub: string, max: number) => {
    if (!limits) return null
    const l: LimitSetting = limits[key]
    return (
      <Row
        title={title}
        sub={sub}
        right={
          <span className="flex shrink-0 items-center gap-3">
            <Num value={l.pct} min={0.1} max={max} suffix="%" label={title} onSave={(pct) => putLimits({ ...limits, [key]: { ...l, pct } })} />
            <Switch on={l.on} onChange={(on) => putLimits({ ...limits, [key]: { ...l, on } })} label={title} />
          </span>
        }
      />
    )
  }
  return (
    <>
      <Group title="Orders">
        <Rows>
          <Row title="Orders wait for your approval" sub="No agent can change this" right={<Switch on disabled label="Orders wait for approval" />} />
        </Rows>
      </Group>

      <Group title="Limits for workers" note={limits?.enabled ? 'A plan past a limit is not offered' : 'Shown on each plan, not held'}>
        <Rows>
          <Row title="Hold new plans to these limits" sub="Off: each limit is still worked out and shown on the plan, as information" right={limits && <Switch on={limits.enabled} onChange={(enabled) => putLimits({ ...limits, enabled })} label="Hold new plans to these limits" />} />
          {limit('maxWorkerRiskPct', 'Most one plan may lose', 'Of the account’s value, at its stops', 100)}
          {limit('maxOpenRiskPct', 'Most all running plans may lose together', 'Of the account’s value', 100)}
          {limit('maxSymbolExposurePct', 'Most of the account in one stock', 'What you hold and what workers may still buy', 500)}
          {limit('maxDailyLossPct', 'No new plans after a losing day', 'Once today’s result is below this share of the account', 100)}
        </Rows>
      </Group>

      <Group title="Plans without a stop" note="Always held">
        <Rows>
          <Row title="Most one of them may cost" sub="With no stop, the whole cost is what can be lost" right={limits && <Num value={limits.noStopCap.perTradeUsd} min={1} max={10_000_000} prefix="$" width={108} label="Most a plan without a stop may cost" onSave={(perTradeUsd) => putLimits({ ...limits, noStopCap: { ...limits.noStopCap, perTradeUsd } })} />} />
          <Row title="Most all of them may cost together" right={limits && <Num value={limits.noStopCap.totalUsd} min={1} max={100_000_000} prefix="$" width={108} label="Most all plans without a stop may cost" onSave={(totalUsd) => putLimits({ ...limits, noStopCap: { ...limits.noStopCap, totalUsd } })} />} />
        </Rows>
      </Group>

      <Group title="Protection for new workers" note="Each plan can say otherwise">
        <Rows>
          <Row title="Break-even step" sub="Near its target, a lot’s stop moves to break-even, fees included" right={<Switch on={ins.on} onChange={(on) => putIns({ on })} label="Break-even step" />} />
          {ins.on && <Row title="…once it has come this far" sub="Of the way from the entry to the target" right={<Choose value={`${ins.level}`} options={pick([75, 85, 90, 95].includes(ins.level) ? [75, 85, 90, 95] : [75, 85, 90, 95, ins.level].sort((a, b) => a - b), '%')} onChange={(v) => putIns({ level: Number(v) })} width={110} />} />}
          {ins.on && (
            <Row
              title="The way is measured to"
              right={
                <Choose
                  value={ins.basis}
                  options={[
                    ['own', 'each lot’s own target'],
                    ['first', 'the first target'],
                  ]}
                  onChange={(basis) => putIns({ basis })}
                  width={190}
                />
              }
            />
          )}
          <Row title="Protect the others after a profit" sub="Once one lot is sold at a gain, the other stops are pulled up to break-even" right={<Switch on={ins.protectOthers} onChange={(protectOthers) => putIns({ protectOthers })} label="Protect the others after a profit" />} />
          <Row title="Sell when it stalls near the target" sub="Takes the zone just under the target instead of waiting for the exact price" right={<Switch on={stall.on} onChange={(on) => putIns({ stall: { ...stall, on } })} label="Sell when it stalls near the target" />} />
          {stall.on && <Row title="…once it is this sure" sub="That ordinary noise would have reached the target by now" right={<Choose value={`${stall.confidence}`} options={pick([50, 70, 80, 90], '%')} onChange={(v) => putIns({ stall: { ...stall, confidence: Number(v) as 50 | 70 | 80 | 90 } })} width={110} />} />}
          {stall.on && (
            <Row
              title="How near the target the zone starts"
              sub="In the stock’s own usual moves"
              right={
                <Choose
                  value={`${stall.sigmas}`}
                  options={[
                    ['0.5', 'half a move'],
                    ['1', 'one move'],
                    ['2', 'two moves'],
                    ['3', 'three moves'],
                  ]}
                  onChange={(v) => putIns({ stall: { ...stall, sigmas: Number(v) } })}
                  width={150}
                />
              }
            />
          )}
        </Rows>
      </Group>

      <Group title="Buy zone for new workers" note="Each plan can say otherwise">
        <Rows>
          <Row
            title="How long a zone is watched"
            sub="Then the worker gives up"
            right={
              <span className="flex shrink-0 items-center gap-2">
                {band.window.kind === 'sessions' && <Num value={band.window.count} min={1} max={60} width={64} label="Sessions to watch" onSave={(count) => putBand({ window: { kind: 'sessions', count: Math.round(count) } })} />}
                <Choose
                  value={band.window.kind === 'gtc' ? 'gtc' : 'sessions'}
                  options={[
                    ['sessions', 'sessions'],
                    ['gtc', 'until stopped'],
                  ]}
                  onChange={(k) => putBand({ window: k === 'gtc' ? { kind: 'gtc' } : { kind: 'sessions', count: band.window.kind === 'sessions' ? band.window.count : 3 } })}
                  width={150}
                />
              </span>
            }
          />
          <Row title="Price under the zone" right={<Choose value={band.below} options={SIDES} onChange={(below) => putBand({ below })} width={150} />} />
          <Row title="Price over the zone" right={<Choose value={band.above} options={SIDES} onChange={(above) => putBand({ above })} width={150} />} />
          <Row
            title="Counts as inside when"
            right={
              <Choose
                value={band.confirm}
                options={[
                  ['5m', 'a 5-minute bar closes inside'],
                  ['live', 'the price touches it'],
                ]}
                onChange={(confirm) => putBand({ confirm })}
                width={230}
              />
            }
          />
          <Row
            title="Room around the zone"
            sub="How far outside still counts"
            right={
              <span className="flex shrink-0 items-center gap-2">
                {band.margin.kind === 'usd' && <Num value={band.margin.usd} min={0.01} max={100} prefix="$" width={84} label="Room around the zone" onSave={(usd) => putBand({ margin: { kind: 'usd', usd } })} />}
                <Choose
                  value={band.margin.kind === 'usd' ? 'usd' : 'auto'}
                  options={[
                    ['auto', 'worked out from the stock'],
                    ['usd', 'a fixed amount'],
                  ]}
                  onChange={(k) => putBand({ margin: k === 'usd' ? { kind: 'usd', usd: band.margin.kind === 'usd' ? band.margin.usd : 0.1 } : { kind: 'auto' } })}
                  width={210}
                />
              </span>
            }
          />
          <Row title="Wait after the open" sub="No buying in the first minutes of a session" right={<Choose value={`${band.skipOpenMin}`} options={pick([0, 5, 15, 30].includes(band.skipOpenMin) ? [0, 5, 15, 30] : [0, 5, 15, 30, band.skipOpenMin].sort((a, b) => a - b), ' min')} onChange={(v) => putBand({ skipOpenMin: Number(v) })} width={110} />} />
          <Row title="Stop buying before the close" right={<Choose value={`${band.stopBeforeCloseMin}`} options={pick([0, 15, 30].includes(band.stopBeforeCloseMin) ? [0, 15, 30] : [0, 15, 30, band.stopBeforeCloseMin].sort((a, b) => a - b), ' min')} onChange={(v) => putBand({ stopBeforeCloseMin: Number(v) })} width={110} />} />
          <Row title="Look before the open" sub="Tells you if the pre-market price has already left the zone" right={<Switch on={band.premarket} onChange={(premarket) => putBand({ premarket })} label="Look before the open" />} />
          <Row title="A note at the end of each day" sub="Where the price stood against the zone" right={<Switch on={band.dailyNote} onChange={(dailyNote) => putBand({ dailyNote })} label="A note at the end of each day" />} />
        </Rows>
      </Group>

      {admin && (
        <Group title="Fees" note="Used for the estimates on a plan">
          <Rows>
            <Row title="IBKR commission plan" right={<Choose value={plan} options={COMMISSION_PLANS.map((p) => [p, COMMISSION_PLAN_LABEL[p]] as const)} onChange={(commissionPlan) => void save({ commissionPlan })} width={200} />} />
          </Rows>
        </Group>
      )}
    </>
  )
}
